/**
 * Teaches PixiJS that the `app://` (Electron) scheme is a URL.
 *
 * Pixi v7's `utils.path.isUrl` is hardcoded to `/^https?:/`, so under
 * the native shell (origin `app://localhost`) Pixi's URL helpers misread
 * the scheme and drop the authority:
 *
 *   - `path.rootname('app://localhost/')` returns `'app://'` instead
 *     of `'app://localhost/'`.
 *   - `path.toAbsolute('/images/x.png')` therefore joins against
 *     `'app://'`, re-normalizes, and produces `'app://images/x.png'`
 *     (the document's host is gone, the leading `/` is gone).
 *   - Pixi then tries to `fetch('app://images/x.png')`, the custom
 *     protocol handler returns garbage, and `createImageBitmap` rejects
 *     with `InvalidStateError`.
 *
 * Widening `isUrl` is sufficient in theory because `path.rootname`
 * branches on it, but we also wrap `rootname` and `toAbsolute` directly
 * as a belt-and-suspenders so the patch works even if a chunk-splitting
 * boundary or a frozen-namespace edge case prevents the `isUrl`
 * mutation from being seen by Pixi's internals.
 *
 * No-op on regular web builds (the regex match is the only extra cost).
 */
import { utils } from 'pixi.js';

let applied = false;

const NATIVE_SCHEME_RE = /^app:\/\//i;

export const applyNativePixiPatch = (): void => {
  if (applied) return;
  applied = true;
  const path = utils.path;

  const originalIsUrl = path.isUrl.bind(path);
  path.isUrl = (value: string): boolean => {
    if (originalIsUrl(value)) return true;
    return /^app:/i.test(value);
  };

  const originalRootname = path.rootname.bind(path);
  path.rootname = (value: string): string => {
    if (typeof value === 'string' && NATIVE_SCHEME_RE.test(value)) {
      // Preserve the scheme + authority + first slash, e.g. `app://localhost/`.
      const afterScheme = value.indexOf('://') + 3;
      const slashAfterHost = value.indexOf('/', afterScheme);
      if (slashAfterHost === -1) {
        return value.endsWith('/') ? value : value + '/';
      }
      return value.slice(0, slashAfterHost + 1);
    }
    return originalRootname(value);
  };

  const originalToAbsolute = path.toAbsolute.bind(path);
  path.toAbsolute = (url: string, customBaseUrl?: string, customRootUrl?: string): string => {
    // If the consumer (or the document base URL) is one of our native
    // schemes, resolve manually using the standard URL constructor so
    // we never round-trip through Pixi's POSIX path helpers, which
    // mishandle the authority.
    if (typeof url === 'string') {
      if (NATIVE_SCHEME_RE.test(url)) return url;
      const base = customBaseUrl ?? customRootUrl ?? (typeof document !== 'undefined' ? document.baseURI : '');
      if (typeof base === 'string' && NATIVE_SCHEME_RE.test(base)) {
        try {
          return new URL(url, base).href;
        } catch {
          // Fall through to Pixi's resolver if URL construction fails.
        }
      }
    }
    return originalToAbsolute(url, customBaseUrl, customRootUrl);
  };
};
